/**
 * web/src/api/tracked-emails.ts
 * Typed clients for /api/tracked-emails/* (API-03). Server contract: src/api/tracked-emails.ts.
 *
 * No role filter on GET — internal tool, all roles see all emails.
 * Confirm is unrestricted by role too; either Maddie or an ops person
 * can mark welcome / ops emails confirmed.
 */
import { api } from '../lib/api';
import type { NudgePlanEntry, NudgePreview, TrackedEmail, TrackedEmailStatus } from './types';

/** Optional filters for GET /api/tracked-emails. */
export interface ListTrackedEmailsQuery {
  eventId?: string;
  status?: TrackedEmailStatus;
  /** Scope to one workflow (the GateReview sent-view / follow-up plan source). */
  workflowId?: string;
}

/**
 * Lists tracked emails (server caps at 200 rows ordered by createdAt desc).
 *
 * @param q optional eventId / status / workflowId filters
 * @returns TrackedEmail[] (each row includes embedded `workflow` projection)
 * @throws ApiClientError on non-2xx
 * @sideEffects HTTP GET
 * @idempotent yes
 */
export async function listTrackedEmails(q: ListTrackedEmailsQuery = {}): Promise<TrackedEmail[]> {
  const params = new URLSearchParams();
  if (q.eventId) params.set('eventId', q.eventId);
  if (q.status) params.set('status', q.status);
  if (q.workflowId) params.set('workflowId', q.workflowId);
  const qs = params.toString();
  return api<TrackedEmail[]>(`/api/tracked-emails${qs ? `?${qs}` : ''}`);
}

/**
 * Marks a tracked email CONFIRMED and resumes the paused waiting_event step
 * (delegates to TrackedEmailService.manualConfirm — see src/email/tracker.ts).
 *
 * @param id TrackedEmail id
 * @returns { ok: true } on success
 * @throws ApiClientError(404) if email not found;
 *         (409) INVALID_STATE_TRANSITION if email already CONFIRMED
 * @sideEffects HTTP POST; engine resumes the paused workflow asynchronously
 * @idempotent no — second call 409s against the now-CONFIRMED row
 */
export async function confirmEmail(id: string): Promise<{ ok: true }> {
  return api(`/api/tracked-emails/${id}/confirm`, { method: 'POST' });
}

/**
 * Full-replaces a tracked email's nudge plan (P2 nudge substrate). The client
 * sends the COMPLETE new entry list — shift dates, cancel entries, add
 * entries, cancel all — and the server swaps it in wholesale.
 *
 * @param id TrackedEmail id
 * @param nudges the complete replacement plan
 * @returns { nudgePlan } — the stored (normalized) plan
 * @throws ApiClientError(404) if email not found;
 *         (409) INVALID_STATE_TRANSITION if a stored `sent` entry was
 *         altered or removed (sent history is immutable)
 * @sideEffects HTTP PATCH; one UPDATE on the TrackedEmail row
 * @idempotent yes — re-sending the same plan is a no-op rewrite
 */
export async function updateNudgePlan(
  id: string,
  nudges: NudgePlanEntry[],
): Promise<{ nudgePlan: NudgePlanEntry[] }> {
  return api(`/api/tracked-emails/${id}/nudges`, {
    method: 'PATCH',
    body: JSON.stringify({ nudges }),
  });
}

/**
 * Fires one planned nudge entry immediately (the "Send now" button). Skips
 * the dueAt gate and the per-event kill switch but keeps every other check
 * (auto-cancel conditions, live-state compose, claim-then-send, and the
 * inbound-activity safety gate — someone we're chasing already wrote 409s).
 *
 * @param id TrackedEmail id
 * @param index zero-based position of the entry in the plan
 * @param force skip ONLY the inbound-activity gate (the "Send anyway" button
 *        after the reviewer has seen the evidence)
 * @returns { status, nudgePlan } — outcome + the updated plan
 * @throws ApiClientError(404) if email not found; (409)
 *         INVALID_STATE_TRANSITION if the entry isn't planned, an
 *         auto-cancel check tripped, inbound activity was found, or
 *         composing/sending failed
 * @sideEffects HTTP POST; one Gmail send + plan mutation + audit entry
 * @idempotent no — a second call 409s against the now-sent entry
 */
export async function sendNudgeNow(
  id: string,
  index: number,
  force = false,
): Promise<{ status: string; nudgePlan: NudgePlanEntry[] }> {
  return api(`/api/tracked-emails/${id}/nudges/${index}/send${force ? '?force=true' : ''}`, {
    method: 'POST',
  });
}

/**
 * Live-computes what one planned nudge would send RIGHT NOW (the review sheet
 * behind "Send now"/"Preview"). Strictly read-only on the server — nothing is
 * claimed, cancelled, audited, or stamped; the render is recomputed again at
 * actual send time.
 *
 * @param id TrackedEmail id
 * @param index zero-based position of the entry in the plan
 * @returns NudgePreview — subject/body/recipients + asset outstanding/satisfied
 * @throws ApiClientError(404) if email not found; (409)
 *         INVALID_STATE_TRANSITION if the entry isn't planned or the live
 *         compose is unavailable (e.g. monday board unreadable)
 * @sideEffects HTTP GET only
 * @idempotent yes
 */
export async function fetchNudgePreview(id: string, index: number): Promise<NudgePreview> {
  return api(`/api/tracked-emails/${id}/nudges/${index}/preview`);
}
